import { ArrowDropDown, Cancel, Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Paper,
  Popover,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import React from 'react'
import { connect } from 'react-redux'
import {
  Operations as IncidentReportOperations,
  operations as incidentReportOperations
} from 'states/dux/incident-report/operations'
import { Payload as IncidentReportPayload } from 'states/dux/incident-report/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import State from 'states/types'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  incidentReport: IncidentReportPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    incidentReport: IncidentReportOperations
  }
}

type AppProps = StateProps & DispatchProps

const IncidentReportsSearchBarFC: React.FC<any> = (props: AppProps) => {
  const { layout, operations } = props
  const [keyword, setKeyword] = React.useState('')
  const [fromDate, setFromDate] = React.useState<Date>()
  const [toDate, setToDate] = React.useState<Date>()
  const [minDate, setMinDate] = React.useState<Date>()
  const [typeShow, setTypeShow] = React.useState(false)
  const typeBtn = React.useRef(null)
  const [statusShow, setStatusShow] = React.useState(false)
  const statusBtn = React.useRef(null)
  const [statusFilter, setStatusFilter] = React.useState({
    pending: { value: true, title: 'Pending', code: 'PEN' },
    accepted: { value: true, title: 'Accepted', code: 'ACC' },
    assigned: { value: true, title: 'Assigned', code: 'ASS' },
    inProgress: { value: true, title: 'In-Progress', code: 'INP' },
    resolved: { value: true, title: 'Resolved', code: 'RES' }
  })
  const [typeFilter, setTypeFilter] = React.useState({
    cleaning: { value: true, title: 'Cleaning', code: 'CL' },
    maintenance: { value: true, title: 'Maintenance', code: 'MN' },
    repair: { value: true, title: 'Repair', code: 'RP' }
  })

  const getStatusFilterArray = (): string[] => {
    const statusFilterArr: string[] = []

    Object.values(statusFilter).forEach(filter => {
      if (filter.value) {
        statusFilterArr.push(filter.code)
      }
    })

    return statusFilterArr
  }

  const getTypeFilterArray = (): string[] => {
    const typeFilterArr: string[] = []

    Object.values(typeFilter).forEach(filter => {
      if (filter.value) {
        typeFilterArr.push(filter.code)
      }
    })

    return typeFilterArr
  }

  const getSearchFilter = (): string => {
    if (keyword !== '') {
      return `&search=${keyword}`
    }

    return ''
  }

  const getDateFilter = (): string => {
    if (fromDate instanceof Date && toDate instanceof Date) {
      return `&date_in=${fromDate.toUTCString()}-${toDate.toUTCString()}`
    }
    return ''
  }

  const getFilterString = (): string => {
    return (
      '?required_action__in=' +
      getTypeFilterArray().join(',') +
      '&status__in=' +
      getStatusFilterArray().join(',') +
      getSearchFilter() +
      getDateFilter()
    )
  }

  const onSubmit = (): void => {
    const filterStr = getFilterString()

    operations.incidentReport.loading(true)

    // Fetch filtered Data
    void get('/report/' + filterStr)
      .then(data => {
        const parsedData = data.map((report: Object) => {
          return objectifyDateStringFields(report)
        })
        operations.incidentReport.set(parsedData)
        operations.incidentReport.loading(false)
      })
      .catch(err => {
        console.log(err)
        operations.incidentReport.loading(false)
      })
  }

  // Code below handles minimum date, should be one day ahead of fromDate
  React.useEffect(() => {
    if (fromDate !== null && fromDate instanceof Date) {
      const tempDate = new Date(fromDate)
      tempDate.setDate(fromDate.getDate() + 1)
      setMinDate(tempDate)
    }
  }, [fromDate])

  React.useEffect(() => {
    if (minDate !== null && minDate instanceof Date) {
      if (toDate !== null && toDate instanceof Date) {
        if (minDate > toDate) {
          setToDate(minDate)
        }
      } else {
        setToDate(minDate)
      }
    }
  }, [minDate])

  return (
    <Box>
      <Box display={'flex'} height={50} padding={1}>
        <Box
          flexGrow={1}
          display={'flex'}
          alignItems={'center'}
          border={2}
          borderRadius={4}
          borderColor={'#d4d2d2'}
          marginX={1}
        >
          <Box flexGrow={1}>
            <TextField
              InputProps={{
                startAdornment: <Search />
              }}
              sx={{
                width: '100%',
                backgroundColor: 'transparent',
                outline: 0,
                '.MuiOutlinedInput-notchedOutline': {
                  borderWidth: '0 !important'
                }
              }}
              type={'text'}
              placeholder={'Report title...'}
              value={keyword}
              onChange={event => setKeyword(event.currentTarget.value)}
            />
          </Box>
          <Box
            paddingLeft={1}
            borderRight={1}
            height={'33px'}
            color={'#d4d2d2'}
          />
          <Box
            paddingRight={1}
            borderLeft={1}
            height={'35px'}
            color={'#d4d2d2'}
          />
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            flexGrow={1}
          >
            <Typography paddingRight={1}>From:</Typography>
            <DatePicker
              value={fromDate instanceof Date ? fromDate : null}
              onChange={value => {
                if (value != null) {
                  setFromDate(value)
                }
              }}
              renderInput={_props => (
                <TextField
                  {..._props}
                  sx={{
                    width: 165,
                    backgroundColor: 'transparent',
                    outline: 0,
                    '.MuiOutlinedInput-notchedOutline': {
                      borderWidth: '0 !important'
                    }
                  }}
                />
              )}
            />
            <Typography paddingX={1}>To:</Typography>
            <DatePicker
              disabled={!(fromDate instanceof Date)}
              value={toDate instanceof Date ? toDate : null}
              minDate={minDate}
              onChange={value => (value != null ? setToDate(value) : null)}
              renderInput={_props => (
                <TextField
                  {..._props}
                  sx={{
                    width: 165,
                    backgroundColor: 'transparent',
                    outline: 0,
                    '.MuiOutlinedInput-notchedOutline': {
                      borderWidth: '0 !important'
                    }
                  }}
                />
              )}
            />
            <Tooltip title={'CLEAR DATE'} placement={'bottom'}>
              <IconButton
                onClick={() => {
                  setToDate(undefined)
                  setFromDate(undefined)
                }}
              >
                <Cancel />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            paddingLeft={1}
            borderRight={1}
            height={'33px'}
            color={'#d4d2d2'}
          />
          <Box
            paddingRight={1}
            borderLeft={1}
            height={'35px'}
            color={'#d4d2d2'}
          />
          <Button
            ref={statusBtn}
            variant={'outlined'}
            sx={{ border: '0 !important', textTransform: 'none', width: 200 }}
            endIcon={<ArrowDropDown />}
            onClick={() => setStatusShow(true)}
          >
            Status: ALL
          </Button>
          <Box
            paddingLeft={1}
            borderRight={1}
            height={'33px'}
            color={'#d4d2d2'}
          />
          <Box
            paddingRight={1}
            borderLeft={1}
            height={'35px'}
            color={'#d4d2d2'}
          />
          <Button
            ref={typeBtn}
            variant={'outlined'}
            sx={{ border: '0 !important', textTransform: 'none', width: 200 }}
            endIcon={<ArrowDropDown />}
            onClick={() => setTypeShow(true)}
          >
            Type: ALL
          </Button>
          <Box
            paddingLeft={1}
            borderRight={1}
            height={'33px'}
            color={'#d4d2d2'}
          />
          <Box
            paddingRight={1}
            borderLeft={1}
            height={'35px'}
            color={'#d4d2d2'}
          />
          <Button
            variant={'outlined'}
            sx={{ border: '0 !important', textTransform: 'none', width: 200 }}
            endIcon={<ArrowDropDown />}
          >
            Tracked: YES
          </Button>
        </Box>
        <Button
          variant={'outlined'}
          size={'large'}
          startIcon={<Search />}
          sx={{ borderRadius: 4 }}
          onClick={() => onSubmit()}
        >
          SEARCH
        </Button>
      </Box>
      <Popover
        open={typeShow}
        onClose={() => setTypeShow(false)}
        anchorEl={typeBtn.current}
        elevation={8}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        sx={{ marginTop: 1 }}
      >
        <Paper
          elevation={8}
          sx={{ width: 1, zIndex: 100 }}
          onMouseLeave={() => setTypeShow(false)}
        >
          <Box
            width={150}
            display={'flex'}
            flexDirection={'column'}
            padding={3}
          >
            <Box display={'flex'} alignItems={'center'}>
              <Checkbox
                checked={typeFilter.repair.value}
                onChange={(event, checked) =>
                  setTypeFilter({
                    ...typeFilter,
                    repair: { ...typeFilter.repair, value: checked }
                  })
                }
              />
              <Typography>Repair</Typography>
            </Box>
            <Box display={'flex'} alignItems={'center'}>
              <Checkbox
                checked={typeFilter.cleaning.value}
                onChange={(event, checked) =>
                  setTypeFilter({
                    ...typeFilter,
                    cleaning: { ...typeFilter.cleaning, value: checked }
                  })
                }
              />
              <Typography>Cleaning</Typography>
            </Box>
            <Box display={'flex'} alignItems={'center'}>
              <Checkbox
                checked={typeFilter.maintenance.value}
                onChange={(event, checked) =>
                  setTypeFilter({
                    ...typeFilter,
                    maintenance: { ...typeFilter.maintenance, value: checked }
                  })
                }
              />
              <Typography>Maintenance</Typography>
            </Box>
          </Box>
        </Paper>
      </Popover>
      <Popover
        open={statusShow}
        onClose={() => setStatusShow(false)}
        anchorEl={statusBtn.current}
        elevation={8}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        sx={{ marginTop: 1 }}
      >
        <Paper
          elevation={8}
          sx={{ width: 1, zIndex: 100 }}
          onMouseLeave={() => setStatusShow(false)}
        >
          <Box
            width={150}
            display={'flex'}
            flexDirection={'column'}
            padding={3}
          >
            <Box display={'flex'} alignItems={'center'}>
              <Checkbox
                checked={statusFilter.pending.value}
                onChange={(event, checked) =>
                  setStatusFilter({
                    ...statusFilter,
                    pending: { ...statusFilter.pending, value: checked }
                  })
                }
              />
              <Typography>{layout.language.translations.pending}</Typography>
            </Box>
            <Box display={'flex'} alignItems={'center'}>
              <Checkbox
                checked={statusFilter.accepted.value}
                onChange={(event, checked) =>
                  setStatusFilter({
                    ...statusFilter,
                    accepted: { ...statusFilter.accepted, value: checked }
                  })
                }
              />
              <Typography>Accepted</Typography>
            </Box>
            <Box display={'flex'} alignItems={'center'}>
              <Checkbox
                checked={statusFilter.assigned.value}
                onChange={(event, checked) =>
                  setStatusFilter({
                    ...statusFilter,
                    assigned: { ...statusFilter.assigned, value: checked }
                  })
                }
              />
              <Typography>Assigned</Typography>
            </Box>
            <Box display={'flex'} alignItems={'center'}>
              <Checkbox
                checked={statusFilter.inProgress.value}
                onChange={(event, checked) =>
                  setStatusFilter({
                    ...statusFilter,
                    inProgress: { ...statusFilter.inProgress, value: checked }
                  })
                }
              />
              <Typography>In-Progress</Typography>
            </Box>
            <Box display={'flex'} alignItems={'center'}>
              <Checkbox
                checked={statusFilter.resolved.value}
                onChange={(event, checked) =>
                  setStatusFilter({
                    ...statusFilter,
                    resolved: { ...statusFilter.resolved, value: checked }
                  })
                }
              />
              <Typography>Resolved</Typography>
            </Box>
          </Box>
        </Paper>
      </Popover>
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    incidentReport: state.incidentReport
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  incidentReport: incidentReportOperations
})

const IncidentReportsSearchBar = connect(
  mapStateToProps,
  mapDispatchToProps
)(IncidentReportsSearchBarFC)

export default IncidentReportsSearchBar
